import { PublicClientApplication } from "@azure/msal-browser";
import { GoogleLogin } from "@react-oauth/google";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Divider } from "semantic-ui-react";

import ENV from "config/Env";
import { createFormHelper } from "lib/Form";
import { LoginFormContent } from "modules/Login/components/LoginForm/components";
import { useCredentialsLoginMutation, useGoogleLoginMutation, useMicrosoftLoginMutation } from "modules/Login/components/LoginForm/hooks";
import "modules/Login/components/LoginForm/LoginForm.scss";
import { QRLogin } from "modules/Login/components/QrLogin";
import { getBrowserLanguage } from "utils/common";

import type { CredentialResponse } from "@react-oauth/google";
import type { DefaultError } from "@tanstack/react-query";

export type LoginFormPayload = {
  username: string;
  password: string;
};

const validateLoginForm = (loginPayload: LoginFormPayload) => {
  if (!loginPayload.username || !loginPayload.password) return false;
  return true;
};

const FormHelper = createFormHelper<LoginFormPayload>();

export const LoginForm = ({ isModalOpen = false }) => {
  const { t } = useTranslation();

  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [googleButtonWidth, setGoogleButtonWidth] = useState(0);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleLoginError = (error: DefaultError) => setErrorMsg(t(`v8_${error.message}`) || error.message);

  const { mutate: credentialsLogin, isPending: isCredentialsLoginPending } = useCredentialsLoginMutation(handleLoginError);
  const { mutate: googleLogin } = useGoogleLoginMutation(handleLoginError);
  const { mutate: microsoftLogin, isPending: isMicrosoftLoginPending } = useMicrosoftLoginMutation(handleLoginError);

  const initMsalInstance = () => {
    try {
      const msalInstance = new PublicClientApplication({
        auth: { redirectUri: window.location.href, clientId: ENV.microsoftClientID, navigateToLoginRequestUrl: false },
        cache: { ...{ cacheLocation: "sessionStorage" } }
      });

      setMsalInstance(msalInstance);
    } catch (error) {
      console.error("Failed to instantiate MSAL PublicClientApplication", error);
    }
  };

  const setGoogleSignInButtonWidth = () => {
    const loginButton = document.getElementById("google-login-button");
    setGoogleButtonWidth(loginButton ? loginButton.clientWidth : 341);
  };

  useEffect(() => {
    initMsalInstance();
    setGoogleSignInButtonWidth();
    window.addEventListener("resize", setGoogleSignInButtonWidth);
    return () => window.removeEventListener("resize", setGoogleSignInButtonWidth);
  }, []);

  const onSubmit = (loginPayload: LoginFormPayload) => {
    if (!validateLoginForm(loginPayload)) return setErrorMsg(t("v8_fill_username_password", "Fill in username and password"));
    credentialsLogin(loginPayload);
  };

  const handleGoogleResponse = (token: CredentialResponse) => {
    if (!token.credential) return;

    googleLogin({ id_token: token.credential });
  };

  const handleMicrosoftResponse = async () => {
    try {
      if (!msalInstance) return null;

      await msalInstance.initialize();
      const data = await msalInstance.loginPopup({ scopes: ["openid", "email", "profile", "user.read"] });

      if (data.idToken) microsoftLogin({ id_token: data.idToken });
    } catch (e: Error | unknown) {
      toast.error(e instanceof Error ? e.message : t("v8_something_went_wrong", "Something went wrong, please contact the administrator."));
    }
  };

  return (
    <div className="LoginContainer">
      <FormHelper.Form defaultValues={{ username: "", password: "" }} onSubmit={onSubmit}>
        <LoginFormContent
          isModalOpen={isModalOpen}
          isLoginPending={isCredentialsLoginPending}
          errorMsg={errorMsg}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          setErrorMsg={setErrorMsg}
        />
      </FormHelper.Form>

      <Divider horizontal className="divider">
        <span>{t("v8_or", "Or")}</span>
      </Divider>

      <div className="mt-15" id="google-login-button">
        <GoogleLogin width={googleButtonWidth} onSuccess={handleGoogleResponse} locale={getBrowserLanguage()} />
      </div>

      <div className="mt-15" id="microsoft-login-button">
        <Button className="light" loading={isMicrosoftLoginPending} fluid onClick={handleMicrosoftResponse}>
          {t("v8_sign_with_microsoft", "Sign in with Microsoft")}
        </Button>
      </div>

      <div className="mt-15">
        <QRLogin />
      </div>

      {isModalOpen && (
        <div className="mt-25">
          <div className="warning-message">
            <p>{t("v8_session_expired_login_again", "Your session has expired, please log in again.")}</p>
          </div>
        </div>
      )}
    </div>
  );
};
